'use client';

import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations } from '@/lib/i18n';

export default function TrackSection() {
  const { language } = useLanguage();
  const t = getTranslations(language)?.track;

  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(2);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref?.current) obs?.observe(ref?.current);
    return () => obs?.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => setActiveStep(3), 1800);
    return () => clearTimeout(timer);
  }, [visible]);

  return (
    <section id="track" ref={ref} className="section-glow py-32 px-6 bg-dispatch-surface/10">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left: Map + ETA card */}
          <div className={`${visible ? 'reveal-blur is-visible' : 'reveal-blur'} order-2 lg:order-1`}>
            <div className="glass-panel rounded-2xl overflow-hidden border border-dispatch-cyan/20 shadow-card">
              <div className="relative h-56 overflow-hidden">
                <AppImage
                  src="https://images.unsplash.com/photo-1536989231345-2e581accf43b"
                  alt="Aerial view of Chicago at night with illuminated street grid"
                  fill
                  className="object-cover opacity-40" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-dispatch-surface/80" />

                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 220">
                  <defs>
                    <linearGradient id="routeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.8" />
                      <stop offset="100%" stopColor="#00FF9D" stopOpacity="0.6" />
                    </linearGradient>
                  </defs>
                  <path d="M 60 170 C 100 150, 160 100, 220 90 S 320 60, 360 50" fill="none" stroke="url(#routeGrad)" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 4" />
                  <g transform="translate(210,85)">
                    <circle cx="0" cy="0" r="12" fill="#00D4FF" opacity="0.2" />
                    <circle cx="0" cy="0" r="6" fill="#00D4FF" />
                    <circle cx="0" cy="0" r="14" fill="none" stroke="#00D4FF" strokeWidth="1" opacity="0.4" />
                  </g>
                  <g transform="translate(360,50)">
                    <circle cx="0" cy="0" r="8" fill="#00FF9D" opacity="0.3" />
                    <circle cx="0" cy="0" r="4" fill="#00FF9D" />
                  </g>
                  <g transform="translate(60,170)">
                    <circle cx="0" cy="0" r="5" fill="rgba(0,212,255,0.5)" />
                  </g>
                </svg>

                <div className="absolute top-4 right-4 glass-panel rounded-xl px-4 py-3 border border-dispatch-cyan/30">
                  <p className="text-dispatch-cyan font-manrope font-extrabold text-2xl">8 min</p>
                  <p className="text-dispatch-muted font-mono text-[10px] uppercase tracking-widest">{t?.eta}</p>
                </div>

                <div className="absolute top-4 left-4 glass-panel rounded-xl px-3 py-2 border border-dispatch-surface flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-400 badge-pulse" />
                  <span className="text-dispatch-text font-mono text-xs">{t?.enRoute}</span>
                </div>
              </div>

              <div className="px-6 py-5">
                <div className="flex items-center justify-between relative">
                  <div className="absolute top-3 left-0 right-0 h-0.5 bg-dispatch-surface" />
                  <div
                    className="absolute top-3 left-0 h-0.5 bg-dispatch-cyan transition-all duration-1000"
                    style={{ width: `${activeStep / (t?.steps?.length - 1) * 100}%` }} />
                  {t?.steps?.map((label, i) => (
                    <div key={label} className="relative flex flex-col items-center gap-2 z-10">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-500 ${
                        i <= activeStep ? 'bg-dispatch-cyan shadow-cyan-sm' : 'bg-dispatch-surface border border-dispatch-surface'
                      }`}>
                        {i <= activeStep ? (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-dispatch-bg">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : (
                          <div className="w-2 h-2 rounded-full bg-dispatch-faint" />
                        )}
                      </div>
                      <span className={`font-mono text-[9px] uppercase tracking-widest whitespace-nowrap ${
                        i <= activeStep ? 'text-dispatch-cyan' : 'text-dispatch-faint'
                      }`}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mx-5 mb-5 bg-dispatch-bg/60 rounded-xl px-4 py-3 border border-dispatch-surface flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-dispatch-cyan/20 flex items-center justify-center shrink-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00D4FF" strokeWidth="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </div>
                <div>
                  <p className="text-dispatch-text font-dm-sans text-xs font-semibold">{t?.notifTitle}</p>
                  <p className="text-dispatch-muted font-dm-sans text-xs mt-0.5">{t?.notifBody}</p>
                  <p className="text-dispatch-faint font-mono text-[10px] mt-1">{t?.notifTime}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: copy + chips */}
          <div className={`flex flex-col gap-8 order-1 lg:order-2 ${visible ? 'reveal-up is-visible' : 'reveal-up'}`}>
            <div>
              <span className="chip mb-4 inline-flex">{t?.chip}</span>
              <h2 className="font-manrope font-extrabold text-dispatch-text leading-tight tracking-tight mt-4"
                style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)' }}>
                {t?.headline1}<br />
                <span className="text-dispatch-cyan">{t?.headline2}</span><br />
                {t?.headline3}
              </h2>
              <p className="text-dispatch-muted text-lg font-dm-sans leading-relaxed mt-4 max-w-md">
                {t?.subtitle}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {t?.chips?.map((c) => <span key={c} className="chip">{c}</span>)}
            </div>

            <div className="glass-panel rounded-xl px-5 py-4 border-l-2 border-dispatch-cyan">
              <p className="text-dispatch-text font-dm-sans text-sm leading-relaxed italic">
                {t?.testimonial}
              </p>
              <p className="text-dispatch-faint font-mono text-[10px] mt-2 uppercase tracking-widest">{t?.testimonialAuthor}</p>
            </div>

            <a href="#download" className="self-start flex items-center gap-3 px-6 py-3.5 bg-dispatch-cyan text-dispatch-bg rounded-xl font-bold font-mono text-sm tracking-wide hover:bg-white transition-all duration-300 shadow-cyan-sm">
              {t?.cta}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}